// Stay marquee: an endless sideways scroll of its first .stay-marquee__group (styles in
// component-stay-marquee.css). The group is copied (aria-hidden, out of the tab order) until the copies fill the
// viewport plus one group, and the row moves by exactly one group's width per loop, so the seam
// never shows. Speed is in px per second (data-speed), so it's the same at every width.
// - Static (the list centred and wrapping) without JS, with reduced motion and whenever the shared motion
//   switch is off (html.stay-motion: the theme editor, "Reveal sections on scroll" off, see theme.liquid).
// - Pauses while the mouse is over it or focus is inside it (CSS); no pause button of its own (2026-10-08).
//   setPaused() is for a parent that stops several rows together (the photo marquee: hover / focus in its section).
// - data-draggable (the photo marquee): the row is moved by script instead of the CSS animation, so it can also
//   be dragged with the mouse (grab), swiped, moved with a sideways trackpad gesture (wheel deltaX; the page's
//   vertical scroll is never taken: the wheel listener is passive) and with the arrow keys when it has focus.
//   After a drag it glides on a little (inertia) and then keeps scrolling from where it stopped. The position
//   is always wrapped to one group's width, so the loop stays seamless. Auto-scroll pauses on hover, while
//   dragging, while focused and while the parent stops it (setPaused); with reduced motion (or the motion switch off) there's
//   no auto-scroll and no inertia, but dragging, swiping and the keys still work.
// - data-axis="vertical" (the Our mission page's photo reel, 2026-10-19): the same loop upwards. The group is a
//   column, copied until the copies fill the marquee's height plus one group, and the track moves up by one group's
//   height per loop (component-stay-marquee.css). The marquee's height comes from its parent, never from its
//   content. Images in the copies get an empty alt (the copies are aria-hidden anyway). Not draggable.
// - Safe to load more than once.
if (!customElements.get('stay-marquee')) {
  customElements.define(
    'stay-marquee',
    class StayMarquee extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.stay-marquee__track');
        this.group = this.querySelector('.stay-marquee__group');
        if (!this.track || !this.group) return;

        this.vertical = this.dataset.axis === 'vertical';
        this.draggable = !this.vertical && this.hasAttribute('data-draggable');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.update = this.update.bind(this);
        this.width = 0;

        this.resizeObserver = new ResizeObserver(() => {
          // Sideways: the width; upwards: the height too
          const width = this.vertical ? `${this.clientWidth}x${this.clientHeight}` : this.clientWidth;
          if (width === this.width) return;
          this.width = width;
          this.update();
        });
        this.resizeObserver.observe(this);
        this.reducedMotion.addEventListener('change', this.update);
        document.fonts?.ready.then(this.update);

        // The motion switch can drop after load (theme.liquid's 3s fallback); other class changes are ignored
        this.motionOn = document.documentElement.classList.contains('stay-motion');
        this.motionObserver = new MutationObserver(() => {
          const motionOn = document.documentElement.classList.contains('stay-motion');
          if (motionOn === this.motionOn) return;
          this.motionOn = motionOn;
          this.update();
        });
        this.motionObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

        if (this.draggable) this.setUpDragging();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.reducedMotion?.removeEventListener('change', this.update);
        this.motionObserver?.disconnect();
        cancelAnimationFrame(this.frame);
      }

      // Auto-scroll and inertia are motion; dragging isn't
      get motionAllowed() {
        return !this.reducedMotion.matches && document.documentElement.classList.contains('stay-motion');
      }

      update() {
        this.track.querySelectorAll('[data-stay-marquee-copy]').forEach((copy) => copy.remove());

        if (!this.draggable && !this.motionAllowed) {
          this.classList.remove('is-running');
          return;
        }

        // Measure the group on one line (upwards: as one column)
        this.classList.add('is-running');
        const groupRect = this.group.getBoundingClientRect();
        const groupWidth = this.vertical ? groupRect.height : groupRect.width;
        if (!groupWidth) return;

        const copies = Math.ceil((this.vertical ? this.clientHeight : this.clientWidth) / groupWidth) + 1;
        for (let i = 0; i < copies; i++) {
          const copy = this.group.cloneNode(true);
          // Hidden from screen readers and out of the tab order
          copy.setAttribute('aria-hidden', 'true');
          copy.querySelectorAll('a, button, [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
          copy.dataset.stayMarqueeCopy = '';
          if (this.vertical) copy.querySelectorAll('img').forEach((image) => (image.alt = ''));
          // No duplicate ids or editor hooks in the copies
          [copy, ...copy.querySelectorAll('[id], [data-shopify-editor-block]')].forEach((element) => {
            element.removeAttribute('id');
            element.removeAttribute('data-shopify-editor-block');
          });
          this.track.append(copy);
        }

        const speed = Number(this.dataset.speed) || 40;
        this.groupWidth = groupWidth;
        this.style.setProperty('--stay-marquee-distance', `${groupWidth}px`);
        this.style.setProperty('--stay-marquee-duration', `${groupWidth / speed}s`);

        if (this.draggable) {
          this.classList.add('is-draggable');
          // Focusable only when there's more than fits on screen
          if (groupWidth > this.clientWidth) this.setAttribute('tabindex', '0');
          else this.removeAttribute('tabindex');
          this.render();
        }
      }

      setPaused(paused) {
        this.classList.toggle('is-paused', paused);
      }

      setUpDragging() {
        this.offset = 0; // px the row has moved to the left, wrapped to [0, groupWidth)
        this.inertia = 0; // px per second, decaying after a drag
        this.target = null; // an arrow-key destination, eased towards
        this.hovered = false;
        this.focused = false;
        this.dragging = null;
        this.wheelUntil = 0;
        this.direction = this.dataset.direction === 'right' ? -1 : 1;
        this.last = performance.now();

        // Hover pauses (a mouse only)
        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.hovered = true;
        });
        this.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse') this.hovered = false;
        });
        this.addEventListener('focusin', () => (this.focused = true));
        this.addEventListener('focusout', () => (this.focused = this.matches(':focus-within')));

        this.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          if (!this.groupWidth) return;
          this.dragging = {
            id: event.pointerId,
            startX: event.clientX,
            startOffset: this.offset,
            moved: false,
            samples: [{ x: event.clientX, t: performance.now() }],
          };
          this.inertia = 0;
          this.target = null;
          // No text selection or native image drag with the mouse; touch keeps the page's vertical scroll
          if (event.pointerType === 'mouse') event.preventDefault();
        });

        this.addEventListener('pointermove', (event) => {
          const drag = this.dragging;
          if (!drag || drag.id !== event.pointerId) return;
          const dx = event.clientX - drag.startX;
          if (!drag.moved && Math.abs(dx) > 4) {
            drag.moved = true;
            this.classList.add('is-dragging');
            this.setPointerCapture?.(event.pointerId);
          }
          if (!drag.moved) return;
          this.offset = drag.startOffset - dx;
          const now = performance.now();
          drag.samples.push({ x: event.clientX, t: now });
          while (drag.samples.length > 2 && now - drag.samples[0].t > 100) drag.samples.shift();
          this.render();
        });

        const end = (event, keepMomentum) => {
          const drag = this.dragging;
          if (!drag || drag.id !== event.pointerId) return;
          this.dragging = null;
          this.classList.remove('is-dragging');
          if (!drag.moved) return;
          // A drag isn't a click on whatever was under the pointer
          this.addEventListener('click', (click) => (click.preventDefault(), click.stopPropagation()), {
            capture: true,
            once: true,
          });
          if (keepMomentum && this.motionAllowed) {
            const first = drag.samples[0];
            const last = drag.samples[drag.samples.length - 1];
            const time = (last.t - first.t) / 1000;
            // A gentle glide: the release speed, damped and capped, fades over about a quarter of a second
            if (time > 0) this.inertia = Math.max(-900, Math.min(900, (-(last.x - first.x) / time) * 0.6));
          }
        };
        this.addEventListener('pointerup', (event) => end(event, true));
        this.addEventListener('pointercancel', (event) => end(event, false));

        // A sideways trackpad gesture moves the row; vertical wheel movement is left to the page (passive)
        this.addEventListener(
          'wheel',
          (event) => {
            if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
            this.offset += event.deltaX;
            this.inertia = 0;
            this.target = null;
            this.wheelUntil = performance.now() + 600;
            this.render();
          },
          { passive: true }
        );

        // Arrow keys: about a card's width per press
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          if (event.target !== this) return;
          event.preventDefault();
          const step = Math.min(320, this.clientWidth * 0.6) * (event.key === 'ArrowRight' ? 1 : -1);
          this.inertia = 0;
          if (this.motionAllowed) {
            this.target = (this.target ?? this.offset) + step;
          } else {
            this.offset += step;
            this.render();
          }
        });

        const tick = (now) => {
          const dt = Math.min(0.05, (now - this.last) / 1000);
          this.last = now;
          if (this.groupWidth && !this.dragging) {
            const speed = Number(this.dataset.speed) || 40;
            if (this.target !== null) {
              const gap = this.target - this.offset;
              this.offset += gap * Math.min(1, dt * 8);
              if (Math.abs(gap) < 0.5) this.target = null;
            } else if (Math.abs(this.inertia) > speed) {
              this.offset += this.inertia * dt;
              this.inertia *= Math.exp(-dt / 0.28);
            } else {
              this.inertia = 0;
              const auto =
                this.motionAllowed &&
                !this.hovered &&
                !this.focused &&
                !this.classList.contains('is-paused') &&
                now > this.wheelUntil;
              if (auto) this.offset += speed * this.direction * dt;
            }
            this.render();
          }
          this.frame = requestAnimationFrame(tick);
        };
        this.frame = requestAnimationFrame(tick);
      }

      render() {
        if (!this.draggable || !this.groupWidth) return;
        this.offset = ((this.offset % this.groupWidth) + this.groupWidth) % this.groupWidth;
        if (this.target !== null && this.target !== undefined) {
          // Keep the eased destination in the same wrap as the position
          const gap = this.target - this.offset;
          this.target = this.offset + (((gap % this.groupWidth) + this.groupWidth * 1.5) % this.groupWidth) - this.groupWidth / 2;
        }
        this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
      }
    }
  );
}
